import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, DatePicker, Image, Spin, Empty, message, ConfigProvider } from "antd";
import dayjs from "dayjs";
import Header from "../../../component/menu/Header";
import SideMenu from "../../../component/menu/SideMenu";
import { constants, ROLES } from "../../../constant/constants";
import InventoryTable from "../../../component/inventory/InventoryTable";
import ApiService from "../../../utils/ApiService";
import useUserStore from "../../../stores/store";
import InventoryConfirmModal from "../../../component/inventory/InventoryConfirmModal";
import { preparePurchaseReport, formatSummaryData } from "../../../services/inventory/purchase-items.service";
import CustomLoader from "../../../component/menu/CustomLoader";
import { getParsedNumber, isDate1GreaterThanDate2 } from "../../../utils/Common";
import { downloadPDF } from "../../../services/inventory/inventory.service";

export default function PurchaseItemReports() {
  const { restaurantId, user, showNotification, role, setNetworkCall } = useUserStore();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [purchaseItemsReport, setPurchaseItemsReport] = useState();
  const [listLoading, setListLoading] = useState(true);
  const [selecteDeleteId, setSelecteDeleteId] = useState("");
  const [filters, setFilters] = useState({
    restaurantId,
    searchKey: "",
    fromDate: "",
    toDate: "",
    page: 1,
    perPage: 10,
  });
  const [openDelModal, setOpenDelModal] = useState(false);
  const [reportSummationData, setReportSummationData] = useState({});

  function onHandlePageChange(page, pageSize) {
    setPage(page);
    setPerPage(pageSize);
    const params = {
      ...filters,
      page,
      perPage: pageSize,
    };

    getPurchaseItemsReport(params);
  }

  async function getPurchaseItemsReport(params = null) {
    try {
      setListLoading(true);

      const res = await ApiService.GET_INVENTORY_PURCHASE_ITEMS_REPORT(params, user.token.token);
      if (res.code !== 200) {
        showNotification(res.data.message);
        setListLoading(false);
        return;
      }
      const { payload } = res;
      const { page, perPage, total, records } = payload;
      setPage(page);
      setPerPage(perPage);
      setTotal(total);
      setPurchaseItemsReport(preparePurchaseReport(records));
      setListLoading(false);
    } catch (err) {
      setListLoading(false);
      console.log("Error in purchase report. Error", err);
      showNotification(err?.response?.data?.message);
      return;
    }
  }

  async function getPurchaseReportSummation(params = null) {
    const { page, perPage, ...filtered } = params;
    try {
      setListLoading(true);

      const res = await ApiService.INVENTORY_PURCHASE_REPORT_SUMMATION(filtered, user.token.token);
      if (res.code !== 200) {
        showNotification(res.data.message);
        setListLoading(false);
        return;
      }
      const { payload } = res;
      const { record } = payload;
      setReportSummationData(formatSummaryData(record));
      setListLoading(false);
    } catch (err) {
      setListLoading(false);
      console.log("Error in purchase report. Error", err);
      showNotification(err?.response?.data?.message);
      return;
    }
  }

  useEffect(() => {
    async function fetchData() {
      const params = {
        ...filters,
        searchKey: "",
        page,
        perPage,
      };
      const summationParams = await getPurchaseItemsReport(params);
      await getPurchaseReportSummation(params);
    }
    fetchData();
  }, []);

  async function onHandleDeleteItem(_id) {
    if (role === ROLES.STAFF) {
      showNotification("You are not authorize to delete this ingredient.");
      return;
    }

    setOpenDelModal(true);
    setSelecteDeleteId(_id);
  }

  function onHandleSearchText(e) {
    const val = e.target.value;
    setFilters((prev) => ({ ...prev, searchKey: val, page: 1 }));
  }

  function onHandleFromDate(date, dateString) {
    if (isDate1GreaterThanDate2(dateString, filters.toDate)) {
      showNotification("From Date cannot be greater than To Date.");
      return;
    }

    setFilters((prev) => ({ ...prev, fromDate: dateString, page: 1 }));
  }

  function onHandleToDate(date, dateString) {
    setFilters((prev) => ({
      ...prev,
      toDate: dateString,
      page: 1,
    }));
  }

  function onHandleSearch() {
    setPurchaseItemsReport();
    const { searchKey, fromDate, toDate } = filters;
    getPurchaseItemsReport({ ...filters, searchKey: searchKey.trim() });
    getPurchaseReportSummation({ ...filters, searchKey: searchKey.trim() });
  }

  function onHandleAdd() {
    navigate("/inventory/purchase-item/add");
    return;
  }

  function clearFilterFields() {
    const params = {
      ...filters,
      searchKey: "",
      fromDate: "",
      toDate: "",
      page: 1,
    };

    setFilters({ ...params });
  }

  function onHandleClear() {
    clearFilterFields();
    getPurchaseItemsReport({ restaurantId, searchKey: "" });
    getPurchaseReportSummation({ restaurantId, searchKey: "" });
  }

  async function deletePurchaseItem(purchaseItemId) {
    setNetworkCall(true);
    try {
      const res = await ApiService.INVENTORY_PURCHASE_ITEM_DELETE(purchaseItemId, user.token.token);
      if (res.code !== 200) {
        setNetworkCall(false);
        setOpenDelModal(false);
        showNotification(res.message);
        return;
      }

      setOpenDelModal(false);
      setNetworkCall(false);
      showNotification("Purchase item deleted successfully.", false);
      const { page, perPage, ...params } = filters;
      getPurchaseItemsReport(params);
      return;
    } catch (err) {
      setOpenDelModal(false);
      setNetworkCall(false);
      showNotification(err.response.data.message);
      return;
    }
  }

  function onHandleDeleteConfirm() {
    if (!Boolean(selecteDeleteId)) return;
    if (selecteDeleteId) {
      deletePurchaseItem(selecteDeleteId);
    }
    setSelecteDeleteId("");
  }

  function onHandleCancelDeleteConfirm() {
    setOpenDelModal(false);
    setSelecteDeleteId("");
  }

  async function downloadSummationData(params) {
    try {
      setNetworkCall(true);
      const res = await ApiService.GET_DOWNLOAD_PURCHASE_REPORT(params, user.token.token);
      if (Boolean(res) && res.status !== 200) {
        console.error(res);
        showNotification(res.message);
        setNetworkCall(false);
        return;
      }
      setNetworkCall(false);
      downloadPDF(res.data, "purchase-report.pdf");
      showNotification("Purchase report downloads successfully!", false);
    } catch (err) {
      setNetworkCall(false);
      console.log("Error in purchase report. Error", err);
      showNotification(err?.response?.data?.message);
      return;
    }
  }

  function onHandleDownloadReport() {
    const { page, perPage, ...filtered } = filters;
    downloadSummationData(filtered);
  }
  

  return (
    <div className="flex flex-col">
      <CustomLoader />
      <Header title={`McDonalds Food Chains`} menu={constants.MENU.INVENTORY} currentMenuKey={"T_7_S_7_L_2"} />
      <div className="flex flex-row bg-prewhite">
        <SideMenu title={`McDonalds Food Chain`} menu={constants.MENU.INVENTORY} />
        <ConfigProvider
          theme={{
            components: {
              DatePicker: {
                zIndexPopup: 1,
              },
            },
          }}
        >
          <div className="flex flex-row p-[24px] w-full mt-[80px] ml-[72px]">
            <div className="flex flex-col w-full">
              <h1 className="pt-[26px] pb-[20px] font-bold text-black text-[24px] leading-[24px] cursor-default">
                Purchase Report
              </h1>
              {/* filter section */}
              <div className="mb-[20px] flex items-center justify-between">
                <div className="flex items-center space-x-[20px]">
                  <div className="w-[230px] h-[48px]">
                    <Input
                      name="search"
                      className={"w-full h-full"}
                      value={filters.searchKey || ""}
                      onChange={onHandleSearchText}
                      onPressEnter={onHandleSearch}
                      placeholder="Search keyword"
                    />
                  </div>

                  <div className="w-[146px] h-[48px]">
                    <DatePicker
                      className={"h-full w-full"}
                      showTime={false}
                      onChange={onHandleFromDate}
                      placeholder="From Date"
                      value={filters.fromDate ? dayjs(filters.fromDate) : null}
                      disabledDate={(current) => current && current > dayjs().endOf("day")}
                    />
                  </div>
                  <div className="w-[146px] h-[48px]">
                    <DatePicker
                      className={"h-full w-full"}
                      showTime={false}
                      onChange={onHandleToDate}
                      placeholder="To Date"
                      value={filters.toDate ? dayjs(filters.toDate) : null}
                      disabledDate={(current) => {
                        if (!filters.fromDate) {
                          return current && current > dayjs().endOf("day");
                        }
                        return (
                          (current && current < dayjs(filters.fromDate).startOf("day")) ||
                          current > dayjs().endOf("day")
                        );
                      }}
                    />
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      className="w-[100px] h-[48px] bg-black rounded-[5px] text-white font-bold leading-[16px]"
                      onClick={onHandleClear}
                    >
                      Clear
                    </button>
                    <button
                      className="w-[100px] h-[48px] bg-[#FCBD10] rounded-[5px] text-black font-bold leading-[16px]"
                      onClick={onHandleSearch}
                    >
                      Search
                    </button>
                  </div>
                </div>

                <div className="flex items-center space-x-[43px]">
                  <div className="w-[56px] h-[56px] flex items-center justify-center rounded-full bg-[#FCBD10]">
                    <button className="w-full h-full " onClick={onHandleDownloadReport}>
                      <Image src="/images/download-all.svg" preview={false} height={48} width={48} />
                    </button>
                  </div>
                  <button
                    className="w-[104px] h-[48px] flex items-center space-x-[19.2px] bg-[#2EC114] rounded-[5px] pl-[25px] pr-[15.2px]"
                    onClick={onHandleAdd}
                  >
                    <span className="font-bold text-white leading-[17px]">Add</span>
                    <Image src="/images/icons/add-white.svg" preview={false} alt="add" />
                  </button>
                </div>
              </div>
              {/* filter section ends */}

              {/* summary data */}
              {Object.keys(reportSummationData).length > 0 && (
                <div className="mb-[20px] flex flex-wrap items-center gap-y-3">
                  {Object.values(reportSummationData).map((summary, index) => (
                    <div
                      key={index}
                      className="bg-white min-w-[210px] min-h-[80px] rounded-[12px] border border-px border-[#E9EBEF] flex items-center justify-center mr-[20px]"
                    >
                      <div className="w-full h-full text-center">
                        <h1 className="text-[#696969] font-p_medium text-sm leading-[21px]">{summary.title}</h1>
                      <p className="text-black text-[20px] leading-[30px] font-p_bold pl-1">
                        {getParsedNumber(summary.amount).toLocaleString(2)} TK
                      </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* summary data ends */}

              <div className="min-h-[90vh]">
                {(listLoading && !Boolean(purchaseItemsReport)) ? (
                  <div className="h-[50vh] flex items-center justify-center">
                    <Spin size="large" />
                  </div>
                ) : (purchaseItemsReport?.length === 0) ? (
                  <div className="h-[50vh] flex items-center justify-center">
                    <Empty />
                  </div>
                ) : (purchaseItemsReport?.length > 0) && (
                  <div>
                    <InventoryTable
                      data={purchaseItemsReport}
                      coloredCols={["SN", "Item Name", "Carriage Inwards", "Purchase Date"]}
                      handleDelete={onHandleDeleteItem}
                      showEditButton={false}
                      paginationRecords={{
                        handlePageChange: onHandlePageChange,
                        page: page,
                        perPage: perPage,
                        setPerPage: setPerPage,
                        total: total,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </ConfigProvider>
      </div>
      <InventoryConfirmModal
        open={openDelModal}
        title="Are you sure?"
        message="This will remove the purchase item report permanently."
        onConfirm={onHandleDeleteConfirm}
        onCancel={onHandleCancelDeleteConfirm}
      />
    </div>
  );
}
